import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { remainingProposalSeconds } from '../../../utils/appointmentWorkflow';

const ComparisonField = ({ label, value }) => (
  <Box sx={{ py: 0.75 }}>
    <Typography variant="caption" sx={{ color: '#8a6a54' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 700, color: '#2f241e' }}>{value || '—'}</Typography>
  </Box>
);

const formatDuration = (value) => (value === null || value === undefined ? '—' : `${value} min`);
const formatPrice = (value) => (value === null || value === undefined ? '—' : `${value} DA`);
const formatDateTime = (value) => (
  value && moment.parseZone(value).isValid()
    ? moment.parseZone(value).format('DD/MM/YYYY HH:mm')
    : '—'
);

export default function AppointmentProposalCard({
  appointment,
  proposal,
  labels,
  onAccept,
  onCancel,
  busy = false,
  error,
}) {
  const seconds = remainingProposalSeconds(proposal.expires_at);
  const proposedService = {
    name: proposal.proposed_service_name || appointment.service_name,
    price: proposal.proposed_service_price ?? appointment.service_price,
    duration: proposal.proposed_service_duration ?? appointment.service_duration,
  };

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 4, backgroundColor: '#fffdf9', borderColor: '#efc8a8' }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ color: '#5e351f', fontWeight: 800 }}>
          {labels.title}
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mt: 2,
        }}>
          <Box sx={{ p: 2, borderRadius: 3, backgroundColor: '#f7f3ef' }}>
            <Chip size="small" label={labels.original} sx={{ mb: 1 }} />
            <ComparisonField label={labels.service} value={appointment.service_name} />
            <ComparisonField label={labels.employee} value={appointment.team_member_name} />
            <ComparisonField label={labels.dateTime} value={formatDateTime(appointment.start_time)} />
            <ComparisonField label={labels.duration} value={formatDuration(appointment.service_duration)} />
            <ComparisonField label={labels.price} value={formatPrice(appointment.service_price)} />
          </Box>
          <Box sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: '#fff4e8',
            border: '1px solid #efc8a8',
          }}>
            <Chip size="small" color="warning" label={labels.proposed} sx={{ mb: 1 }} />
            <ComparisonField label={labels.service} value={proposedService.name} />
            <ComparisonField label={labels.employee} value={proposal.proposed_team_member_name} />
            <ComparisonField label={labels.dateTime} value={formatDateTime(proposal.proposed_start_time)} />
            <ComparisonField label={labels.duration} value={formatDuration(proposedService.duration)} />
            <ComparisonField label={labels.price} value={formatPrice(proposedService.price)} />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {labels.deadline}: {formatDateTime(proposal.expires_at)}
          </Typography>
          <Typography variant="body2">
            {labels.remaining}: {Math.ceil(seconds / 60)} min
          </Typography>
          {proposal.message && <Typography sx={{ pt: 1 }}>{proposal.message}</Typography>}
        </Stack>
        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent>
      <Divider />
      <CardActions sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="contained"
            disabled={busy || seconds === 0}
            onClick={() => onAccept(proposal.id)}
            sx={{ backgroundColor: '#a45b2a', '&:hover': { backgroundColor: '#82451f' } }}
          >
            {labels.accept}
          </Button>
          <Button disabled={busy} color="error" onClick={() => onCancel(proposal.id)}>
            {labels.cancel}
          </Button>
        </Stack>
      </CardActions>
    </Card>
  );
}
